import { z } from "zod";

/**
 * Plugin manifest (plugin.json) schema — shared by the install endpoint
 * (server-side validation), the plugin gateway (network allowlist), and the
 * frontend plugin host (pages/sidebar registration).
 *
 * Bump HOST_API_VERSION whenever the window.DeeplySmart surface or the SDK
 * changes incompatibly; plugins declare the version they were built against.
 */
export const HOST_API_VERSION = 1;

/** Max compressed zip size accepted by the install endpoint. */
export const PLUGIN_ZIP_MAX_BYTES = 20 * 1024 * 1024;
/** Max total inflated size (zip-bomb guard). */
export const PLUGIN_INFLATED_MAX_BYTES = 50 * 1024 * 1024;
/** Max number of files in a plugin zip. */
export const PLUGIN_MAX_ENTRIES = 200;

/**
 * Page caps, by declared surface (R-038).
 *
 * The public cap exists to keep sidebars and route tables sane for end users.
 * That reasoning does not hold for a back-office CMS, which is internal or
 * white-label-client-facing and is expected to be page-dense — the ecosystem
 * ADMIN CMS alone carries tenants, users, churches, branding, app registry,
 * logs, content ops and billing. Rather than quietly raising the limit for
 * everything, a plugin DECLARES that it is an admin surface and gets a higher,
 * still-bounded cap.
 */
export const PLUGIN_MAX_PAGES = 20;
export const PLUGIN_MAX_PAGES_ADMIN = 60;

export const PLUGIN_ID_REGEX = /^[a-z0-9][a-z0-9-]{1,40}$/;
const SEMVER_REGEX = /^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/;
/** Flat file names inside the zip root (entry points, icon). */
const SAFE_FILENAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

export const BUILTIN_SIDEBAR_SECTIONS = [
  "communications",
  "essential-tools",
  "advanced",
  "utilities",
] as const;

const sidebarSectionSchema = z.union([
  z.enum(BUILTIN_SIDEBAR_SECTIONS),
  z.object({
    root: z.object({
      id: z.string().regex(PLUGIN_ID_REGEX),
      label: z.string().min(1).max(40),
    }),
  }),
]);

const pluginPageSchema = z.object({
  /** Route path; must be namespaced under /plugins/{plugin id}. */
  path: z.string().min(1).max(200),
  title: z.string().min(1).max(60),
  /** null/omitted → routable but not shown in the sidebar. */
  sidebar: z
    .object({
      section: sidebarSectionSchema,
      label: z.string().min(1).max(40),
      order: z.number().int().min(0).max(1000).optional(),
    })
    .nullable()
    .optional(),
});

export const BUILTIN_PLUGIN_PERMISSIONS = [
  "rag",
  "documents",
  "chat",
  "agents",
  "tools",
  "records",
  "storage-kv",
  "secrets",
  "core-api",
  "spaces",
  // S11 (2026-08-15): LDOS Contacts — the city-center CRM utility. Grants
  // sdk.contacts, scoped to the calling plugin's app_slug via
  // ldos_contact_app_links; a plugin sees only contacts its app was granted.
  "contacts",
] as const;

const NETWORK_PERMISSION_REGEX = /^network:[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/i;

const permissionSchema = z.string().refine(
  (p) =>
    (BUILTIN_PLUGIN_PERMISSIONS as readonly string[]).includes(p) ||
    NETWORK_PERMISSION_REGEX.test(p),
  {
    message: `Must be one of ${BUILTIN_PLUGIN_PERMISSIONS.join(", ")} or network:<host>`,
  }
);

const settingsFieldSchema = z.object({
  type: z.enum(["secret", "string", "boolean", "number"]),
  label: z.string().min(1).max(80),
  description: z.string().max(300).optional(),
});

export const pluginManifestSchema = z
  .object({
    id: z.string().regex(PLUGIN_ID_REGEX),
    name: z.string().min(1).max(60),
    version: z.string().regex(SEMVER_REGEX),
    description: z.string().max(500).optional(),
    author: z.string().max(120).optional(),
    homepage: z.string().max(300).optional(),
    hostApiVersion: z.number().int().min(1),
    entry: z.object({
      client: z.string().regex(SAFE_FILENAME_REGEX),
      styles: z.string().regex(SAFE_FILENAME_REGEX).optional(),
    }),
    icon: z.string().regex(SAFE_FILENAME_REGEX).optional(),
    /**
     * What kind of surface this plugin is. Governs the page cap, and is the
     * declared-intent hook the host can later use to render admin plugins
     * differently or hide them from non-admin users.
     *
     * "public" (default) — a normal end-user plugin, capped at PLUGIN_MAX_PAGES.
     * "admin"  — an internal / white-label-client back-office CMS. Not
     *            end-user-facing, so the sidebar-clutter argument behind the
     *            public cap does not apply, and it may declare up to
     *            PLUGIN_MAX_PAGES_ADMIN. Still bounded: unbounded pages means
     *            unbounded routes and nav entries.
     */
    surface: z.enum(["public", "admin"]).default("public"),
    // Hard ceiling here; the REAL per-surface cap is enforced in superRefine so
    // the error message can name which cap was exceeded and why.
    pages: z.array(pluginPageSchema).min(1).max(PLUGIN_MAX_PAGES_ADMIN),
    permissions: z.array(permissionSchema).max(30).default([]),
    settingsSchema: z.record(z.string(), settingsFieldSchema).optional(),
  })
  .superRefine((manifest, ctx) => {
    const cap = manifest.surface === "admin" ? PLUGIN_MAX_PAGES_ADMIN : PLUGIN_MAX_PAGES;
    if (manifest.pages.length > cap) {
      ctx.addIssue({
        code: "custom",
        message:
          manifest.surface === "admin"
            ? `${manifest.pages.length} pages exceeds the admin-surface cap of ${cap}`
            : `${manifest.pages.length} pages exceeds the cap of ${cap}. If this is an internal or white-label back-office CMS rather than an end-user plugin, declare "surface": "admin" (cap ${PLUGIN_MAX_PAGES_ADMIN}).`,
        path: ["pages"],
      });
    }

    const prefix = `/plugins/${manifest.id}`;
    for (const page of manifest.pages) {
      if (page.path !== prefix && !page.path.startsWith(`${prefix}/`)) {
        ctx.addIssue({
          code: "custom",
          message: `Page path "${page.path}" must start with "${prefix}"`,
          path: ["pages"],
        });
      }
    }
  });

export type PluginManifest = z.infer<typeof pluginManifestSchema>;
export type PluginPage = PluginManifest["pages"][number];
export type PluginSidebarSection = z.infer<typeof sidebarSectionSchema>;

export function parsePluginManifest(
  raw: unknown
): { manifest: PluginManifest; error?: undefined } | { manifest?: undefined; error: string } {
  const result = pluginManifestSchema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const where = first?.path?.length ? ` at ${first.path.join(".")}` : "";
    return { error: `Invalid plugin.json${where}: ${first?.message ?? "unknown error"}` };
  }
  if (result.data.hostApiVersion !== HOST_API_VERSION) {
    return {
      error: `Plugin targets host API v${result.data.hostApiVersion}, this app provides v${HOST_API_VERSION}`,
    };
  }
  return { manifest: result.data };
}

/** Hosts the plugin gateway may forward requests to (exact-host match). */
export function manifestAllowedHosts(manifest: Pick<PluginManifest, "permissions">): string[] {
  return (manifest.permissions ?? [])
    .filter((p): p is string => typeof p === "string" && p.startsWith("network:"))
    .map((p) => p.slice("network:".length).toLowerCase());
}

/** Reject zip entry names that could escape or become weird storage keys. */
export function isSafeZipEntryName(name: string): boolean {
  if (!name || name.length > 300) return false;
  if (name.includes("\0") || name.includes("\\")) return false;
  if (name.startsWith("/") || /^[A-Za-z]:/.test(name)) return false;
  const parts = name.split("/");
  return parts.every(
    (part) => part.length > 0 && part !== "." && part !== ".." && /^[A-Za-z0-9._ -]+$/.test(part)
  );
}
